import { z } from 'zod'
import {
  ACCESS_LEVELS,
  AUTHORITY_STATES,
  COMPILE_MODES,
  MEMORY_CLASSES,
  SOURCE_TYPES,
  SYNC_CONFLICT_POLICIES,
  AUTHORITY_EXPIRY_MAX_MS,
} from '../constants'
import { searchDocumentsSchema } from '../search-contract'
import { validateRequiredDescription } from '../utils/description-validation'
import { repositoryUrlSchema } from '../utils/repository-url'
import { utf8ByteLength } from '../utils/utf8-byte-length'

export const MAX_SYNC_CONTENT_BYTES = 1024 * 1024
export const MAX_PREVIEW_SOURCES = 50
export const MAX_PREVIEW_CONTENT_BYTES = 10 * 1024 * 1024

/** Shared UTF-8 payload contract for source, memory, and journal writes. */
export function syncContentSchema({ required = false }: { required?: boolean } = {}) {
  const schema = required ? z.string().min(1) : z.string()
  return schema.refine((content) => utf8ByteLength(content) <= MAX_SYNC_CONTENT_BYTES, {
    message: `Content must be at most 1 MiB (${MAX_SYNC_CONTENT_BYTES} bytes)`,
  })
}

function requiredDescription(maxChars = 1000) {
  return z.string().transform((value, ctx) => {
    const validation = validateRequiredDescription(value, { maxChars })
    if (!validation.ok) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: validation.error })
      return z.NEVER
    }
    return validation.value
  })
}

export const createDocumentSchema = z.object({
  title: z.string().min(1).max(500),
  description: requiredDescription(),
  content: z.string().default(''),
  contentJson: z.unknown().optional(),
  folderId: z.string().optional(),
  category: z.string().max(100).optional(),
  tags: z.array(z.string().max(50)).max(20).optional(),
  accessLevel: z.enum(ACCESS_LEVELS).default('private'),
})

export const updateDocumentSchema = z.object({
  title: z.string().min(1).max(500).optional(),
  description: requiredDescription().optional(),
  content: z.string().optional(),
  contentJson: z.unknown().optional(),
  folderId: z.string().nullable().optional(),
  category: z.string().max(100).nullable().optional(),
  tags: z.array(z.string().max(50)).max(20).optional(),
  accessLevel: z.enum(ACCESS_LEVELS).optional(),
})

export const createFolderSchema = z.object({
  name: z.string().min(1).max(200),
  parentId: z.string().nullable().optional(),
})

export const reorderFolderSchema = z.object({
  parentId: z.string().nullable(),
  position: z.number().int().min(0),
})

export const reorderItemSchema = z.object({
  type: z.enum(['folder', 'document']),
  id: z.string(),
  parentId: z.string().nullable(),
  afterId: z.string().optional(),
  beforeId: z.string().optional(),
})

export const setPreferenceSchema = z.object({
  value: z.string().max(10000),
})

export { searchDocumentsSchema }

export const createShareLinkSchema = z.object({
  documentId: z.string(),
  expiresInDays: z.number().int().min(1).max(90).default(30),
})

export const upsertSourceDocumentSchema = z.object({
  title: z.string().min(1).max(500),
  content: syncContentSchema().default(''),
  contentJson: z.unknown().optional(),
  folderId: z.string().optional(),
  category: z.string().max(100).optional(),
  tags: z.array(z.string().max(50)).max(20).optional(),
  accessLevel: z.enum(ACCESS_LEVELS).default('private'),
  sourceType: z.enum(SOURCE_TYPES),
  sourcePath: z.string().min(1).max(1000),
  repoUrl: repositoryUrlSchema.optional().nullable(),
  branch: z.string().max(200).optional().nullable(),
  commitSha: z.string().max(100).optional().nullable(),
  derivedFromDocumentId: z.string().max(200).optional().nullable(),
  derivedFromSourcePath: z.string().max(1000).optional().nullable(),
  compileMode: z.enum(COMPILE_MODES).default('preserve'),
  memoryClass: z.enum(MEMORY_CLASSES).optional().nullable(),
  syncCommand: z.string().max(1000).optional().nullable(),
  conflictPolicy: z.enum(SYNC_CONFLICT_POLICIES).default('stop'),
  syncBatch: z.never().optional(),
})

export const upsertSourceBatchSchema = z.object({
  sources: z.array(upsertSourceDocumentSchema).min(1).max(200),
})

export const SOURCE_CONFLICT_RESOLUTIONS = ['keep_source', 'keep_local'] as const

export const resolveSourceConflictSchema = z
  .object({
    decision: z.enum(SOURCE_CONFLICT_RESOLUTIONS),
  })
  .strict()

/** Human-only source authority review. Review timestamps are always server-owned. */
export const updateSourceAuthoritySchema = z
  .object({
    state: z.enum(AUTHORITY_STATES),
    expiresAt: z.number().int().nonnegative().max(AUTHORITY_EXPIRY_MAX_MS).nullable().optional(),
    expectedReviewedAt: z.number().int().nonnegative().max(AUTHORITY_EXPIRY_MAX_MS).nullable(),
  })
  .strict()

export const MEMORY_WRITE_CLASSES = [
  'project-doc',
  'decision-record',
  'agent-observation',
  'plan-report',
  'runbook',
] as const

export const JOURNAL_MEMORY_CLASSES = ['journal-entry', 'session-summary'] as const

/** Least-privilege memory write contract. Source/repository and overwrite controls are server-owned. */
export const upsertMemoryDocumentSchema = z
  .object({
    title: z.string().min(1).max(500),
    content: syncContentSchema().default(''),
    memoryClass: z.enum(MEMORY_WRITE_CLASSES),
    category: z.string().max(100).optional(),
    tags: z.array(z.string().max(50)).max(20).optional(),
  })
  .strict()

/** Append-only journal contract. requestId is supplied by the transport and owns identity. */
export const appendJournalDocumentSchema = z
  .object({
    title: z.string().min(1).max(500),
    content: syncContentSchema({ required: true }),
    memoryClass: z.enum(JOURNAL_MEMORY_CLASSES).default('journal-entry'),
    category: z.string().max(100).optional(),
    tags: z.array(z.string().max(50)).max(20).optional(),
  })
  .strict()

export const writeRequestIdSchema = z.string().trim().min(1).max(200)

export const previewSourceDocumentsSchema = z
  .object({
    sources: z.array(upsertSourceDocumentSchema).min(1).max(MAX_PREVIEW_SOURCES),
  })
  .superRefine(({ sources }, ctx) => {
    const aggregateBytes = sources.reduce(
      (total, source) => total + utf8ByteLength(source.content),
      0,
    )
    if (aggregateBytes > MAX_PREVIEW_CONTENT_BYTES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sources'],
        message: `Preview content must total at most 10 MiB (${MAX_PREVIEW_CONTENT_BYTES} bytes)`,
      })
    }
  })
